import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ScrollIndicator from "../components/ScrollIndicator";

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const lineageCards = [
    { num: "I", title: "Vedic Education", body: "Our astrologers are trained in the classical Parashari and Jaimini systems. Each has studied under a recognised Jyotish Acharya with a minimum of seven years of dedicated practice before working with clients." },
    { num: "II", title: "Ritual Authenticity", body: "Every Yagya, Sankalpa, and Daan service is performed by Brahmins trained in their specific Vedic lineage. We verify credentials, we supervise execution, and we document everything — because your faith deserves that level of care." },
    { num: "III", title: "Continuous Integrity Review", body: "We internally review every consultation and every ritual. If a client was recommended something they did not need, or if a ritual was performed below our standard, we address it. Our reputation is built on individual acts of honest care." }
  ];

  const values = [
    { title: "Satya — Truth", body: "We tell you what we see, not what you want to hear. If your chart does not indicate a challenge, we say so. If a remedy is not warranted, we will not suggest one. Truth is not an inconvenience — it is our foundation." },
    { title: "Ahimsa — Non-Harm", body: "We will never harm you with fear, with pressure, or with exaggerated claims. The Vedic tradition explicitly forbids using knowledge to create anxiety. We honour that prohibition, completely." },
    { title: "Viveka — Discernment", body: "Not every problem needs a ritual. Not every chart needs a remedy. We apply discernment to every case — recommending only what is genuinely indicated. This protects both you and the integrity of the tradition." },
    { title: "Seva — Service", body: "We are here to serve — not to profit from your vulnerability. Our success is measured not in revenue but in the genuine relief and clarity our clients experience. That orientation shapes every decision we make." }
  ];

  return (
    <div data-testid="about-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About Us - Amatya | Your Divine Counselor</title>
        <meta name="description" content="Learn about Amatya - rooted in Vedic tradition, committed to truth. Our astrologers are trained in classical Parashari and Jaimini systems with 7+ years of practice." />
        <meta name="keywords" content="About Amatya, Vedic astrology, Jyotish, Parashari system, authentic astrology, spiritual guidance, Indian astrology" />
        <link rel="canonical" href="https://www.amatya.co.in/about" />
        <meta property="og:title" content="About Us - Amatya | Your Divine Counselor" />
        <meta property="og:description" content="Rooted in Vedic tradition, committed to truth. Learn about Amatya's authentic approach to astrology and spiritual guidance." />
        <meta property="og:url" content="https://www.amatya.co.in/about" />
      </Helmet>
      
      {/* Hero */}
      <div className="page-hero" data-testid="about-hero">
        <div className="page-hero-content">
          <span className="page-hero-label">Who We Are</span>
          <h1 className="page-hero-title">Rooted in tradition.<br/><em>Committed to truth.</em></h1>
          <p className="page-hero-sub">Amatya was built on a single belief: that ancient wisdom, practised with modern integrity, can genuinely change lives — without fear, without pressure, and without compromise.</p>
        </div>
      </div>

      {/* Mission */}
      <section className="about-mission" data-testid="about-mission">
        <div className="wrap">
          <div className="about-mission-grid">
            <div className="reveal">
              <h2 className="about-mission-pull">We exist to be the counselor you <em>actually deserve.</em></h2>
            </div>
            <div className="reveal d2">
              <p className="about-mission-body">For generations, Vedic astrology has been a system of profound guidance — precise, honest, and deeply compassionate. It was never meant to be a tool of fear or urgency. It was meant to illuminate.</p>
              <p className="about-mission-body">We created Amatya because too many people had been told to fear their own charts. Too many had paid for rituals they did not understand. Too many had left consultations more anxious than when they arrived.</p>
              <p className="about-mission-body">That is not counsel. That is exploitation. And we refuse to be part of it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Lineage */}
      <div style={{background:'var(--white)'}} className="section-scroll-indicator-light">
        <ScrollIndicator targetId="about-lineage" />
      </div>

      {/* Lineage */}
      <section className="about-lineage" id="about-lineage" data-testid="about-lineage">
        <div className="wrap">
          <div className="about-lineage-intro reveal">
            <span className="section-label">Our Foundation</span>
            <h2 className="section-title">What we stand on</h2>
            <p className="section-body" style={{margin:'16px auto 0',maxWidth:'560px'}}>Every practitioner at Amatya carries a lineage of study, discipline, and verified practice. We do not hire generalists. We do not tolerate shortcuts.</p>
          </div>
          <div className="lineage-grid">
            {lineageCards.map((card, i) => (
              <div key={i} className={`lineage-card reveal ${i > 0 ? `d${i}` : ''}`} data-testid={`lineage-card-${i}`}>
                <div className="lineage-num">{card.num}</div>
                <div className="lineage-title">{card.title}</div>
                <div className="lineage-body">{card.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll to Values */}
      <div style={{background:'var(--parch-mid)'}} className="section-scroll-indicator-light">
        <ScrollIndicator targetId="about-values" />
      </div>

      {/* Values */}
      <section className="about-values" id="about-values" data-testid="about-values">
        <div className="wrap">
          <span className="section-label reveal" style={{color:'rgba(212,168,67,.75)'}}>What We Live By</span>
          <h2 className="process-title reveal d1">Our four sacred values</h2>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className={`value-item reveal ${i > 0 ? `d${i}` : ''}`} data-testid={`value-item-${i}`}>
                <span className="value-mark">✦</span>
                <div className="value-title">{v.title}</div>
                <div className="value-body">{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll to Promise */}
      <ScrollIndicator targetId="about-promise" />

      {/* Promise */}
      <section className="about-promise" id="about-promise" data-testid="about-promise">
        <div className="wrap">
          <div className="about-promise-box reveal">
            <span className="promise-dev">॥ अमात्य ॥</span>
            <div className="promise-text">"One who possesses purity, intelligence, well-tested virtue, and the comprehensive wisdom of a true counselor."</div>
            <p className="promise-body">This is the name we chose — and the standard we hold ourselves to, every single day. When you come to us, you are not a transaction. You are a person who deserves honest, careful, compassionate guidance. We will not rest until that is exactly what you receive.</p>
            <div style={{marginTop:'36px', display:'flex', gap:'14px', flexWrap:'wrap', alignItems:'center'}}>
              <a
                href="https://wa.me/917633997555?text=Hi%20Amatya%2C%20I%20would%20like%20to%20book%20my%20Astro%20Analysis%20reading.%20Please%20share%20the%20next%20steps."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-whatsapp-book-btn"
                className="btn"
                style={{background:'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color:'#fff', border:'none', display:'inline-flex', alignItems:'center', gap:'8px', boxShadow:'0 6px 20px rgba(18, 140, 126, 0.28)'}}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
                Book Your Reading Now!!
              </a>
              <Link to="/begin-journey" className="btn btn-ember" data-testid="about-begin-btn">
                What's Included in Your Consultation <span className="btn-arr">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
